import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { fetchPartners } from '@/services/partnerService';
import { Project } from '@/types/partner';

const PAGE_SIZE = 8;

type ProjectWithPartner = Project & { partner_id: string };

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { tl } = useLanguage();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchPartners,
  });

  const allProjects: ProjectWithPartner[] = useMemo(
    () =>
      partners.reduce<ProjectWithPartner[]>((acc, partner) => {
        if (!partner.projects?.length) return acc;
        return [
          ...acc,
          ...partner.projects.map((project) => ({
            ...project,
            partner_id: partner.id,
          })),
        ];
      }, []),
    [partners]
  );

  const heroProject = useMemo(() => {
    const flagged = allProjects.find((p) => p.is_projects_hero && p.images?.[0]);
    if (flagged) return flagged;
    return allProjects.find((p) => p.images?.[0]) || allProjects[0];
  }, [allProjects]);

  const gridProjects = useMemo(() => {
    const rest = allProjects
      .filter((p) => p.id !== heroProject?.id)
      .sort((a, b) => a.title.localeCompare(b.title));
    return rest;
  }, [allProjects, heroProject?.id]);

  const visibleProjects = gridProjects.slice(0, visibleCount);
  const hasMore = visibleCount < gridProjects.length;

  const openProject = (project: ProjectWithPartner) => {
    navigate(`/partners/${project.partner_id}?project=${project.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white text-black">
      <Helmet>
        <title>{tl('Projets', 'Projects', 'Progetti')} | Meubles Karim</title>
      </Helmet>

      {/* Title */}
      <section className="px-4 pb-8 pt-[88px] text-center lg:pb-10 lg:pt-[104px]">
        <p className="mb-3 text-[11px] uppercase tracking-[0.16em] text-neutral-400">
          <Link to="/" className="transition-colors hover:text-black">
            Meubles Karim
          </Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-600">{tl('Projets', 'Projects', 'Progetti')}</span>
        </p>
        <h1 className="text-2xl font-semibold uppercase tracking-[0.18em] md:text-3xl">
          {tl('Projets', 'Projects', 'Progetti')}
        </h1>
      </section>

      {/* Hero — most important project */}
      {heroProject && (
        <section className="px-3 md:px-5 lg:px-6">
          <button
            type="button"
            onClick={() => openProject(heroProject)}
            className="group relative block aspect-[16/10] w-full overflow-hidden bg-neutral-200 md:aspect-[2.2/1] md:min-h-[420px]"
          >
            {heroProject.images?.[0] ? (
              <img
                src={heroProject.images[0]}
                alt={heroProject.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
            ) : null}
            <div className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/45" />
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <h2 className="max-w-4xl text-2xl font-semibold uppercase tracking-[0.14em] text-white md:text-4xl lg:text-5xl">
                {heroProject.title}
              </h2>
              <span className="mt-6 text-[11px] font-medium uppercase tracking-[0.22em] text-white underline underline-offset-8">
                {tl('Découvrir', 'Discover', 'Scopri')}
              </span>
            </div>
          </button>
        </section>
      )}

      {/* Projects grid — 4 columns */}
      <section className="mx-auto max-w-[1400px] px-4 py-14 md:px-8 md:py-20 lg:px-12">
        {visibleProjects.length === 0 && !heroProject ? (
          <p className="text-center text-sm text-neutral-500">
            {tl('Aucun projet trouvé', 'No projects found', 'Nessun progetto trovato')}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
              {visibleProjects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  className="group text-left"
                  onClick={() => openProject(project)}
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
                    {project.images?.[0] ? (
                      <img
                        src={project.images[0]}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    ) : null}
                    {project.images?.[1] && project.images[1] !== project.images[0] && (
                      <img
                        src={project.images[1]}
                        alt=""
                        aria-hidden
                        className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.1em]">
                      {project.title}
                    </h3>
                    {project.location ? (
                      <p className="mt-1 text-xs text-neutral-600">{project.location}</p>
                    ) : null}
                    {project.project_category && project.project_category !== 'All' ? (
                      <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-neutral-400">
                        {project.project_category}
                      </p>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>

            {hasMore && (
              <div className="mt-16 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="text-xs font-medium uppercase tracking-[0.2em] text-black underline underline-offset-8 transition-opacity hover:opacity-55"
                >
                  {tl('Voir plus', 'Load more', 'Carica altro')}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default Projects;
